import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  catchError,
  firstValueFrom,
  map,
  Observable,
  retry,
  throwError,
  timeout,
  timer,
} from 'rxjs';
import {
  SupplyTypeDto,
  SupplyTypeListQuery,
  SupplyTypeListResult,
  SupplyTypeRequest,
} from './supply-types-api.types';
import { mapSettingsError, SettingsApiError } from './settings-api.errors';
import { SETTINGS_API_URL } from './settings-api.tokens';

const REQUEST_TIMEOUT_MS = 15_000;
const LIST_PAGE_SIZE = 100;
const TYPES_PATH = '/catalogs/supplies/types';

@Injectable({ providedIn: 'root' })
export class SupplyTypesApiService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${inject(SETTINGS_API_URL).replace(/\/+$/, '')}${TYPES_PATH}`;

  list(query: SupplyTypeListQuery = {}): Observable<SupplyTypeListResult> {
    return this.http
      .get<unknown>(this.baseUrl, { params: this.listParams(query) })
      .pipe(
        timeout(REQUEST_TIMEOUT_MS),
        retry({ count: 1, delay: error => this.retryDelay(error) }),
        map(body => unwrapList(body)),
        catchError(error => throwError(() => mapSettingsError(error))),
      );
  }

  getById(uuid: string): Promise<SupplyTypeDto> {
    return this.request(
      this.http.get<unknown>(`${this.baseUrl}/${uuid}`),
      body => unwrapItem(body),
    );
  }

  create(payload: SupplyTypeRequest): Promise<SupplyTypeDto> {
    return this.request(
      this.http.post<unknown>(this.baseUrl, payload),
      body => unwrapItem(body),
    );
  }

  update(uuid: string, payload: SupplyTypeRequest): Promise<SupplyTypeDto> {
    return this.request(
      this.http.put<unknown>(`${this.baseUrl}/${uuid}`, payload),
      body => unwrapItem(body),
    );
  }

  activate(uuid: string): Promise<void> {
    return this.request(
      this.http.patch<unknown>(`${this.baseUrl}/${uuid}/activate`, null),
      () => undefined,
    );
  }

  inactivate(uuid: string): Promise<void> {
    return this.request(
      this.http.patch<unknown>(`${this.baseUrl}/${uuid}/inactivate`, null),
      () => undefined,
    );
  }

  remove(uuid: string): Promise<void> {
    return this.request(
      this.http.delete<unknown>(`${this.baseUrl}/${uuid}`),
      () => undefined,
    );
  }

  private async request<T>(
    source: Observable<unknown>,
    project: (body: unknown) => T,
  ): Promise<T> {
    try {
      const body = await firstValueFrom(source.pipe(timeout(REQUEST_TIMEOUT_MS)));
      return project(body);
    } catch (error) {
      throw mapSettingsError(error);
    }
  }

  private listParams(query: SupplyTypeListQuery): HttpParams {
    let params = new HttpParams()
      .set('page', String(query.page ?? 0))
      .set('size', String(query.size ?? LIST_PAGE_SIZE));

    if (query.name) params = params.set('name', query.name);
    if (query.description) params = params.set('description', query.description);
    if (query.enabled !== undefined) {
      params = params.set('enabled', String(query.enabled));
    }
    if (query.sort) params = params.set('sort', query.sort);

    return params;
  }

  private retryDelay(error: unknown): Observable<number> {
    if (error instanceof HttpErrorResponse) {
      if (error.status === 429) {
        const retryAfter = Number(error.headers.get('Retry-After'));
        const seconds =
          Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : 1;
        return timer(seconds * 1000);
      }
      if (error.status >= 500 || error.status === 0) {
        return timer(300);
      }
    }
    return throwError(() => error);
  }
}

function unwrapData(body: unknown): unknown {
  if (body && typeof body === 'object' && 'data' in body) {
    return (body as { data: unknown }).data;
  }
  return body;
}

function unwrapList(body: unknown): SupplyTypeListResult {
  const data = unwrapData(body);
  if (Array.isArray(data)) {
    return { items: data as SupplyTypeDto[], total: data.length };
  }

  if (data && typeof data === 'object') {
    const page = data as {
      data?: unknown;
      content?: unknown;
      items?: unknown;
      pagination?: { totalRecords?: unknown };
      totalElements?: unknown;
      total?: unknown;
    };
    const nestedItems = page.data;
    if (Array.isArray(nestedItems)) {
      const totalRecords = page.pagination?.totalRecords;
      return {
        items: nestedItems as SupplyTypeDto[],
        total: typeof totalRecords === 'number' ? totalRecords : nestedItems.length,
      };
    }
    if (Array.isArray(page.content)) {
      const items = page.content as SupplyTypeDto[];
      return {
        items,
        total:
          typeof page.totalElements === 'number' ? page.totalElements : items.length,
      };
    }
    if (Array.isArray(page.items)) {
      const items = page.items as SupplyTypeDto[];
      return {
        items,
        total: typeof page.total === 'number' ? page.total : items.length,
      };
    }
  }

  throw new SettingsApiError('unexpected', 0);
}

function unwrapItem(body: unknown): SupplyTypeDto {
  const data = unwrapData(body);
  if (!data || typeof data !== 'object') {
    throw new SettingsApiError('unexpected', 0);
  }

  const item = data as SupplyTypeDto;
  if (!item.uuid || typeof item.name !== 'string') {
    throw new SettingsApiError('unexpected', 0);
  }
  return item;
}
